import api, { apiRequest } from 'mastodon/api';
import type {
  ApiCommunityJSON,
  ApiCommunityMembershipJSON,
  ApiCommunityRuleJSON,
  ApiCommunityInviteJSON,
  ApiCommunityJoinRequestJSON,
  ApiCommunityReportJSON,
  ApiCommunityBanJSON,
  CommunityPrivacy,
  CommunityJoinMode,
  CommunityRole,
} from 'mastodon/api_types/communities';
import type { ApiStatusJSON } from 'mastodon/api_types/statuses';
import type { AppDispatch } from 'mastodon/store';

import { importFetchedStatuses } from './importer';

export const COMMUNITIES_DIRECTORY_FETCH_REQUEST =
  'COMMUNITIES_DIRECTORY_FETCH_REQUEST';
export const COMMUNITIES_DIRECTORY_FETCH_SUCCESS =
  'COMMUNITIES_DIRECTORY_FETCH_SUCCESS';
export const COMMUNITIES_DIRECTORY_FETCH_FAIL =
  'COMMUNITIES_DIRECTORY_FETCH_FAIL';

export const COMMUNITIES_MINE_FETCH_SUCCESS = 'COMMUNITIES_MINE_FETCH_SUCCESS';

export const COMMUNITY_FETCH_SUCCESS = 'COMMUNITY_FETCH_SUCCESS';

export const COMMUNITY_CREATE_SUCCESS = 'COMMUNITY_CREATE_SUCCESS';
export const COMMUNITY_UPDATE_SUCCESS = 'COMMUNITY_UPDATE_SUCCESS';
export const COMMUNITY_DELETE_SUCCESS = 'COMMUNITY_DELETE_SUCCESS';

export const COMMUNITY_JOIN_SUCCESS = 'COMMUNITY_JOIN_SUCCESS';
export const COMMUNITY_LEAVE_SUCCESS = 'COMMUNITY_LEAVE_SUCCESS';

export const COMMUNITY_TIMELINE_FETCH_REQUEST =
  'COMMUNITY_TIMELINE_FETCH_REQUEST';
export const COMMUNITY_TIMELINE_FETCH_SUCCESS =
  'COMMUNITY_TIMELINE_FETCH_SUCCESS';
export const COMMUNITY_TIMELINE_FETCH_FAIL = 'COMMUNITY_TIMELINE_FETCH_FAIL';

export const COMMUNITY_MEMBERS_FETCH_SUCCESS =
  'COMMUNITY_MEMBERS_FETCH_SUCCESS';

export interface CreateCommunityParams {
  name: string;
  slug: string;
  description?: string;
  privacy: CommunityPrivacy;
  join_mode: CommunityJoinMode;
  rules?: string[];
}

export const fetchCommunityDirectory =
  (q?: string) => async (dispatch: AppDispatch) => {
    dispatch({ type: COMMUNITIES_DIRECTORY_FETCH_REQUEST });
    try {
      const params: Record<string, string> = {};
      if (q) params.q = q;
      const data = await apiRequest<ApiCommunityJSON[]>(
        'GET',
        'v1/communities',
        { params },
      );
      dispatch({
        type: COMMUNITIES_DIRECTORY_FETCH_SUCCESS,
        communities: data,
      });
      return data;
    } catch (err) {
      dispatch({ type: COMMUNITIES_DIRECTORY_FETCH_FAIL, error: err });
      throw err;
    }
  };

export const fetchMyCommunities = () => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiCommunityJSON[]>(
    'GET',
    'v1/communities/mine',
  );
  dispatch({ type: COMMUNITIES_MINE_FETCH_SUCCESS, communities: data });
  return data;
};

export const fetchCommunity = (id: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiCommunityJSON>(
    'GET',
    `v1/communities/${id}`,
  );
  dispatch({ type: COMMUNITY_FETCH_SUCCESS, community: data });
  return data;
};

export const createCommunity =
  (params: CreateCommunityParams) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiCommunityJSON>('POST', 'v1/communities', {
      data: { ...params },
    });
    dispatch({ type: COMMUNITY_CREATE_SUCCESS, community: data });
    return data;
  };

export const updateCommunity =
  (id: string, attrs: Partial<CreateCommunityParams>) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiCommunityJSON>(
      'PUT',
      `v1/communities/${id}`,
      { data: attrs },
    );
    dispatch({ type: COMMUNITY_UPDATE_SUCCESS, community: data });
    return data;
  };

// Multipart upload for the community image. `file === null` removes the
// existing image. Admin-only — the server enforces this via CommunityPolicy.
export const updateCommunityImage =
  (id: string, file: File | null) => async (dispatch: AppDispatch) => {
    const formData = new FormData();
    if (file) {
      formData.append('image', file);
    } else {
      formData.append('remove_image', 'true');
    }
    const { data } = await api().put<ApiCommunityJSON>(
      `/api/v1/communities/${id}`,
      formData,
    );
    dispatch({ type: COMMUNITY_UPDATE_SUCCESS, community: data });
    return data;
  };

export const deleteCommunity =
  (id: string, confirm_name: string) => async (dispatch: AppDispatch) => {
    await apiRequest('DELETE', `v1/communities/${id}`, {
      data: { confirm_name },
    });
    dispatch({ type: COMMUNITY_DELETE_SUCCESS, communityId: id });
  };

export const joinCommunity =
  (id: string, message?: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiCommunityJSON | { state: string }>(
      'POST',
      `v1/communities/${id}/join`,
      { data: message ? { message } : undefined },
    );
    if ('id' in data) {
      dispatch({ type: COMMUNITY_JOIN_SUCCESS, community: data });
    }
    return data;
  };

export const leaveCommunity = (id: string) => async (dispatch: AppDispatch) => {
  await apiRequest('POST', `v1/communities/${id}/leave`);
  dispatch({ type: COMMUNITY_LEAVE_SUCCESS, communityId: id });
};

export const fetchCommunityTimeline =
  (id: string, maxId?: string) => async (dispatch: AppDispatch) => {
    dispatch({ type: COMMUNITY_TIMELINE_FETCH_REQUEST, communityId: id });
    try {
      const params: Record<string, string> = {};
      if (maxId) params.max_id = maxId;
      const data = await apiRequest<ApiStatusJSON[]>(
        'GET',
        `v1/communities/${id}/timeline`,
        { params },
      );
      // Push the raw statuses through the standard importer so they end up
      // in state.statuses (and accounts get normalized too). Then store only
      // the IDs in our timeline slice — same pattern as the home timeline.
      dispatch(importFetchedStatuses(data));
      dispatch({
        type: COMMUNITY_TIMELINE_FETCH_SUCCESS,
        communityId: id,
        statusIds: data.map((s) => s.id),
        maxId,
      });
      return data;
    } catch (err) {
      dispatch({
        type: COMMUNITY_TIMELINE_FETCH_FAIL,
        communityId: id,
        error: err,
      });
      throw err;
    }
  };

export const fetchCommunityMembers =
  (id: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiCommunityMembershipJSON[]>(
      'GET',
      `v1/communities/${id}/members`,
    );
    dispatch({
      type: COMMUNITY_MEMBERS_FETCH_SUCCESS,
      communityId: id,
      members: data,
    });
    return data;
  };

export const updateCommunityMember = async (
  communityId: string,
  membershipId: string,
  role: CommunityRole,
): Promise<ApiCommunityMembershipJSON> =>
  apiRequest<ApiCommunityMembershipJSON>(
    'PATCH',
    `v1/communities/${communityId}/members/${membershipId}`,
    { data: { role } },
  );

export const kickCommunityMember = async (
  communityId: string,
  membershipId: string,
): Promise<void> => {
  await apiRequest(
    'DELETE',
    `v1/communities/${communityId}/members/${membershipId}`,
  );
};

export const fetchCommunityRules = async (
  communityId: string,
): Promise<ApiCommunityRuleJSON[]> =>
  apiRequest<ApiCommunityRuleJSON[]>(
    'GET',
    `v1/communities/${communityId}/rules`,
  );

export const createCommunityRule = async (
  communityId: string,
  text: string,
  position?: number,
): Promise<ApiCommunityRuleJSON> =>
  apiRequest<ApiCommunityRuleJSON>(
    'POST',
    `v1/communities/${communityId}/rules`,
    { data: { text, position } },
  );

export const updateCommunityRule = async (
  communityId: string,
  ruleId: string,
  attrs: { text?: string; position?: number },
): Promise<ApiCommunityRuleJSON> =>
  apiRequest<ApiCommunityRuleJSON>(
    'PUT',
    `v1/communities/${communityId}/rules/${ruleId}`,
    { data: attrs },
  );

export const deleteCommunityRule = async (
  communityId: string,
  ruleId: string,
): Promise<void> => {
  await apiRequest('DELETE', `v1/communities/${communityId}/rules/${ruleId}`);
};

export const inviteToCommunity = async (
  communityId: string,
  accountId: string,
): Promise<ApiCommunityInviteJSON> =>
  apiRequest<ApiCommunityInviteJSON>(
    'POST',
    `v1/communities/${communityId}/invites`,
    { data: { account_id: accountId } },
  );

export const fetchJoinRequests = async (
  communityId: string,
): Promise<ApiCommunityJoinRequestJSON[]> =>
  apiRequest<ApiCommunityJoinRequestJSON[]>(
    'GET',
    `v1/communities/${communityId}/join_requests`,
  );

export const approveJoinRequest = async (
  communityId: string,
  requestId: string,
): Promise<void> => {
  await apiRequest(
    'POST',
    `v1/communities/${communityId}/join_requests/${requestId}/approve`,
  );
};

export const rejectJoinRequest = async (
  communityId: string,
  requestId: string,
): Promise<void> => {
  await apiRequest(
    'POST',
    `v1/communities/${communityId}/join_requests/${requestId}/reject`,
  );
};

export const banFromCommunity = async (
  communityId: string,
  accountId: string,
  reason?: string,
): Promise<void> => {
  await apiRequest('POST', `v1/communities/${communityId}/bans`, {
    data: { account_id: accountId, reason },
  });
};

export const fetchCommunityBans = async (
  communityId: string,
): Promise<ApiCommunityBanJSON[]> =>
  apiRequest<ApiCommunityBanJSON[]>(
    'GET',
    `v1/communities/${communityId}/bans`,
  );

// Admin-only on the server (CommunityPolicy#manage_admins?) — a mod may ban but
// may not undo another moderator's ban.
export const revokeCommunityBan = async (
  communityId: string,
  banId: string,
): Promise<void> => {
  await apiRequest('DELETE', `v1/communities/${communityId}/bans/${banId}`);
};

export const reportInCommunity = async (
  communityId: string,
  args: {
    target_account_id?: string;
    status_id?: string;
    reason: string;
  },
): Promise<ApiCommunityReportJSON> =>
  apiRequest<ApiCommunityReportJSON>(
    'POST',
    `v1/communities/${communityId}/reports`,
    { data: args },
  );

export const fetchCommunityReports = async (
  communityId: string,
  state?: 'open' | 'resolved' | 'dismissed',
): Promise<ApiCommunityReportJSON[]> => {
  const params: Record<string, string> = {};
  if (state) params.state = state;
  return apiRequest<ApiCommunityReportJSON[]>(
    'GET',
    `v1/communities/${communityId}/reports`,
    { params },
  );
};

export const resolveCommunityReport = async (
  communityId: string,
  reportId: string,
): Promise<ApiCommunityReportJSON> =>
  apiRequest<ApiCommunityReportJSON>(
    'POST',
    `v1/communities/${communityId}/reports/${reportId}/resolve`,
  );

export const dismissCommunityReport = async (
  communityId: string,
  reportId: string,
): Promise<ApiCommunityReportJSON> =>
  apiRequest<ApiCommunityReportJSON>(
    'POST',
    `v1/communities/${communityId}/reports/${reportId}/dismiss`,
  );
